import React, { useEffect, lazy, Suspense } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Switch, Route, Redirect, useParams } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import { Dispatch } from "redux";
import { useTranslation } from "react-i18next";

import ErrorBoundary from "./components/error-boundary/error-boundary.component";
import DropMenu from "./components/drop-menu/drop-menu.component";
import Loader from "./components/loader/loader.component";
import Alert from "./components/alert/alert.component";

import { selectCurrentUser } from "./redux/user/user.selectors";
import { setAlert } from "./redux/alert/alert.actions";
import {
  selectIsLoading,
  selectLoadingMessage,
} from "./redux/loading/loading.selectors";
import { checkUserSession } from "./redux/user/user.actions";

import "./App.css";
import Header from "./components/header/header.component";
import Footer from "./components/footer/footer.component";
import HomePage from "./pages/homepage/homepage.component";
import DefaultSeo from "./seo/default-seo.component";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "./i18n/i18n";

const mapStateToProps = createStructuredSelector({
  currentUser: selectCurrentUser,
  isLoading: selectIsLoading,
  loadingMessage: selectLoadingMessage,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  checkUserSession: () => dispatch(checkUserSession()),
  setAlert: (message: string) => dispatch(setAlert(message)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const LangRoutes: React.FC<PropsFromRedux> = ({
  setAlert,
  checkUserSession,
  currentUser,
  isLoading,
  loadingMessage,
}) => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n, t } = useTranslation();

  useEffect(() => {
    if (lang && lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return (
    <div>
      <DefaultSeo />
      <div>
        <ErrorBoundary>
          <Suspense fallback={<Loader />}>
            <Switch>
              <Route
                exact
                path="/:lang"
                render={() => (
                  <div>
                    <Header />
                    <HomePage />
                    <Footer />
                    <DropMenu />
                  </div>
                )}
              />
              <Redirect to={`/${lang}`} />
            </Switch>
          </Suspense>
        </ErrorBoundary>
        {isLoading && <Loader fixed message={loadingMessage} />}
      </div>
      <Alert />
    </div>
  );
};

const ConnectedLangRoutes = connector(LangRoutes);

const App = () => {
  return (
    <Switch>
      <Route
        exact
        path="/"
        render={({ location }) => (
          <Redirect to={{ pathname: `/${DEFAULT_LANG}/`, search: location.search, hash: location.hash }} />
        )}
      />
      <Route
        path={`/:lang(${SUPPORTED_LANGS.join("|")})`}
        render={() => <ConnectedLangRoutes />}
      />
      <Redirect to={`/${DEFAULT_LANG}/`} />
    </Switch>
  );
};

export default App;
